import React, { useEffect, useState } from 'react';
import {
  FiSettings,
  FiMail,
  FiClock,
} from 'react-icons/fi';
import {
  FaSyncAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaTwitter,
  FaFacebook,
  FaInstagram,
  FaDiscord,
  FaApple,
  FaAndroid,
  FaBell,
  FaFlag,
  FaWrench,
  FaGlobe,
  FaSave,
  FaExclamationTriangle,
  FaDatabase,
  FaTrash,
  FaSearch,
  FaServer,
  FaMemory,
  FaKey,
} from 'react-icons/fa';
import apiService, { AppSettings as AppSettingsType } from '../services/apiService';

interface CacheStats {
  enabled: boolean;
  connected: boolean;
  memoryUsage?: {
    used: string;
    peak: string;
    total: string;
  };
  keyCount?: number;
  hitRate?: number;
  uptime?: number;
  keys?: string[];
}

const AppSettings: React.FC = () => {
  const [settings, setSettings] = useState<AppSettingsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Cache Management State
  const [cacheStats, setCacheStats] = useState<CacheStats | null>(null);
  const [loadingCacheStats, setLoadingCacheStats] = useState(false);
  const [flushingCache, setFlushingCache] = useState(false);
  const [deletingPattern, setDeletingPattern] = useState(false);
  const [deletePattern, setDeletePattern] = useState('');
  const [deleteKey, setDeleteKey] = useState('');
  const [deletingKey, setDeletingKey] = useState(false);
  const [clearSeriesId, setClearSeriesId] = useState('');
  const [clearingSeriesCache, setClearingSeriesCache] = useState(false);
  const [clearContestId, setClearContestId] = useState('');
  const [clearingContestCache, setClearingContestCache] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getAppSettings();
      setSettings(data);
      setHasChanges(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Use specific endpoints for different settings sections
      const promises: Promise<any>[] = [];

      // Update maintenance settings
      promises.push(apiService.updateMaintenance({
        isEnabled: settings.maintenance.isEnabled,
        message: settings.maintenance.message,
        allowAdminAccess: settings.maintenance.allowAdminAccess,
      }));

      // Update feature flags
      promises.push(apiService.updateFeatureFlags(settings.featureFlags));

      // Update iOS version
      promises.push(apiService.updateAppVersion('ios', settings.appVersion.ios));

      // Update Android version
      promises.push(apiService.updateAppVersion('android', settings.appVersion.android));

      // Update general settings (support email, URLs, etc.)
      promises.push(apiService.updateAppSettings({
        supportEmail: settings.supportEmail,
        termsOfServiceUrl: settings.termsOfServiceUrl,
        privacyPolicyUrl: settings.privacyPolicyUrl,
        contactUsUrl: settings.contactUsUrl,
        socialLinks: settings.socialLinks,
        defaultCredits: settings.defaultCredits,
        notifications: settings.notifications,
        caching: settings.caching,
        adminCaching: settings.adminCaching,
      }));

      await Promise.all(promises);

      setSuccess('Settings saved successfully!');
      setHasChanges(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSettings = (path: string, value: any) => {
    if (!settings) return;
    setHasChanges(true);

    const keys = path.split('.');
    const newSettings = { ...settings };
    let current: any = newSettings;

    for (let i = 0; i < keys.length - 1; i++) {
      current[keys[i]] = { ...current[keys[i]] };
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;

    setSettings(newSettings);
  };

  // Cache Management Functions
  const fetchCacheStats = async () => {
    try {
      setLoadingCacheStats(true);
      const stats = await apiService.getCacheStats();
      setCacheStats(stats);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch cache stats');
    } finally {
      setLoadingCacheStats(false);
    }
  };

  const handleFlushCache = async () => {
    if (!window.confirm('Are you sure you want to flush ALL cache data? This action cannot be undone.')) {
      return;
    }
    try {
      setFlushingCache(true);
      await apiService.flushCache();
      setSuccess('Cache flushed successfully!');
      fetchCacheStats();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to flush cache');
    } finally {
      setFlushingCache(false);
    }
  };

  const handleDeletePattern = async () => {
    if (!deletePattern.trim()) {
      setError('Please enter a pattern');
      return;
    }
    try {
      setDeletingPattern(true);
      const result = await apiService.deleteCachePattern(deletePattern);
      setSuccess(`Deleted ${result.deletedCount} keys matching pattern "${deletePattern}"`);
      setDeletePattern('');
      fetchCacheStats();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete pattern');
    } finally {
      setDeletingPattern(false);
    }
  };

  const handleDeleteKey = async () => {
    if (!deleteKey.trim()) {
      setError('Please enter a key');
      return;
    }
    try {
      setDeletingKey(true);
      await apiService.deleteCacheKey(deleteKey);
      setSuccess(`Deleted cache key: ${deleteKey}`);
      setDeleteKey('');
      fetchCacheStats();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete key');
    } finally {
      setDeletingKey(false);
    }
  };

  const handleClearSeriesCache = async () => {
    if (!clearSeriesId.trim()) {
      setError('Please enter a series ID');
      return;
    }
    try {
      setClearingSeriesCache(true);
      await apiService.clearSeriesCache(clearSeriesId);
      setSuccess(`Cleared cache for series: ${clearSeriesId}`);
      setClearSeriesId('');
      fetchCacheStats();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear series cache');
    } finally {
      setClearingSeriesCache(false);
    }
  };

  const handleClearContestCache = async () => {
    if (!clearContestId.trim()) {
      setError('Please enter a contest ID');
      return;
    }
    try {
      setClearingContestCache(true);
      await apiService.clearContestCache(clearContestId);
      setSuccess(`Cleared cache for contest: ${clearContestId}`);
      setClearContestId('');
      fetchCacheStats();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear contest cache');
    } finally {
      setClearingContestCache(false);
    }
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center">
          <FaSyncAlt className="h-8 w-8 text-blue-500 animate-spin" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading settings...</p>
        </div>
      </div>
    );
  }

  if (error && !settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <FaTimesCircle className="h-12 w-12 text-red-500 mx-auto" />
          <p className="mt-4 text-red-600 dark:text-red-400">{error}</p>
          <button
            onClick={fetchSettings}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!settings) return null;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <FiSettings className="h-6 w-6" />
            App Settings
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Configure global application settings
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchSettings}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-400 border border-gray-300 dark:border-dark-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-300 transition-colors"
          >
            <FaSyncAlt className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
              hasChanges
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            <FaSave className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="flex items-center gap-2 p-4 bg-green-50 dark:bg-success-500/10 border border-green-200 dark:border-success-500/30 rounded-lg">
          <FaCheckCircle className="h-5 w-5 text-green-600 dark:text-success-500" />
          <span className="text-green-800 dark:text-success-400">{success}</span>
        </div>
      )}
      {error && settings && (
        <div className="flex items-center gap-2 p-4 bg-red-50 dark:bg-danger-500/10 border border-red-200 dark:border-danger-500/30 rounded-lg">
          <FaTimesCircle className="h-5 w-5 text-red-600 dark:text-danger-500" />
          <span className="text-red-800 dark:text-danger-400">{error}</span>
        </div>
      )}

      {/* Unsaved Changes Warning */}
      {hasChanges && (
        <div className="flex items-center gap-2 p-4 bg-yellow-50 dark:bg-warning-500/10 border border-yellow-200 dark:border-warning-500/30 rounded-lg">
          <FaExclamationTriangle className="h-5 w-5 text-yellow-600 dark:text-warning-500" />
          <span className="text-yellow-800 dark:text-warning-400">You have unsaved changes</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* General Settings */}
        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-300/40">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <FaGlobe className="h-5 w-5" />
              General Settings
            </h2>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <FiMail className="inline h-4 w-4 mr-1" />
                Support Email
              </label>
              <input
                type="email"
                value={settings.supportEmail}
                onChange={(e) => updateSettings('supportEmail', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Terms of Service URL
              </label>
              <input
                type="url"
                value={settings.termsOfServiceUrl}
                onChange={(e) => updateSettings('termsOfServiceUrl', e.target.value)}
                placeholder="https://example.com/terms"
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Privacy Policy URL
              </label>
              <input
                type="url"
                value={settings.privacyPolicyUrl}
                onChange={(e) => updateSettings('privacyPolicyUrl', e.target.value)}
                placeholder="https://example.com/privacy"
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Contact Us URL
              </label>
              <input
                type="url"
                value={settings.contactUsUrl}
                onChange={(e) => updateSettings('contactUsUrl', e.target.value)}
                placeholder="https://example.com/contact"
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Default Credits
              </label>
              <input
                type="number"
                value={settings.defaultCredits}
                onChange={(e) => updateSettings('defaultCredits', parseInt(e.target.value) || 0)}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500"
              />
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-300/40">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <FaTwitter className="h-5 w-5" />
              Social Links
            </h2>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <FaTwitter className="inline h-4 w-4 mr-1 text-blue-400" />
                Twitter
              </label>
              <input
                type="url"
                value={settings.socialLinks.twitter}
                onChange={(e) => updateSettings('socialLinks.twitter', e.target.value)}
                placeholder="https://twitter.com/..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <FaFacebook className="inline h-4 w-4 mr-1 text-blue-600" />
                Facebook
              </label>
              <input
                type="url"
                value={settings.socialLinks.facebook}
                onChange={(e) => updateSettings('socialLinks.facebook', e.target.value)}
                placeholder="https://facebook.com/..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <FaInstagram className="inline h-4 w-4 mr-1 text-pink-500" />
                Instagram
              </label>
              <input
                type="url"
                value={settings.socialLinks.instagram}
                onChange={(e) => updateSettings('socialLinks.instagram', e.target.value)}
                placeholder="https://instagram.com/..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <FaDiscord className="inline h-4 w-4 mr-1 text-indigo-500" />
                Discord
              </label>
              <input
                type="url"
                value={settings.socialLinks.discord}
                onChange={(e) => updateSettings('socialLinks.discord', e.target.value)}
                placeholder="https://discord.gg/..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500"
              />
            </div>
          </div>
        </div>

        {/* iOS App Version */}
        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-300/40">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <FaApple className="h-5 w-5" />
              iOS App Version
            </h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Min Version
                </label>
                <input
                  type="text"
                  value={settings.appVersion.ios.minVersion}
                  onChange={(e) => updateSettings('appVersion.ios.minVersion', e.target.value)}
                  placeholder="1.0.0"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Current Version
                </label>
                <input
                  type="text"
                  value={settings.appVersion.ios.currentVersion}
                  onChange={(e) => updateSettings('appVersion.ios.currentVersion', e.target.value)}
                  placeholder="1.0.0"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Update URL
              </label>
              <input
                type="url"
                value={settings.appVersion.ios.updateUrl}
                onChange={(e) => updateSettings('appVersion.ios.updateUrl', e.target.value)}
                placeholder="https://apps.apple.com/..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500"
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-dark-400/30 rounded-lg">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Force Update</span>
              <button
                onClick={() => updateSettings('appVersion.ios.forceUpdate', !settings.appVersion.ios.forceUpdate)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.appVersion.ios.forceUpdate ? 'bg-blue-600' : 'bg-gray-300 dark:bg-dark-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.appVersion.ios.forceUpdate ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Android App Version */}
        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-300/40">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <FaAndroid className="h-5 w-5 text-green-500" />
              Android App Version
            </h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Min Version
                </label>
                <input
                  type="text"
                  value={settings.appVersion.android.minVersion}
                  onChange={(e) => updateSettings('appVersion.android.minVersion', e.target.value)}
                  placeholder="1.0.0"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Current Version
                </label>
                <input
                  type="text"
                  value={settings.appVersion.android.currentVersion}
                  onChange={(e) => updateSettings('appVersion.android.currentVersion', e.target.value)}
                  placeholder="1.0.0"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Update URL
              </label>
              <input
                type="url"
                value={settings.appVersion.android.updateUrl}
                onChange={(e) => updateSettings('appVersion.android.updateUrl', e.target.value)}
                placeholder="https://play.google.com/..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500"
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-dark-400/30 rounded-lg">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Force Update</span>
              <button
                onClick={() => updateSettings('appVersion.android.forceUpdate', !settings.appVersion.android.forceUpdate)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.appVersion.android.forceUpdate ? 'bg-blue-600' : 'bg-gray-300 dark:bg-dark-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.appVersion.android.forceUpdate ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-300/40">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <FaBell className="h-5 w-5" />
              Notifications
            </h2>
          </div>
          <div className="p-6 space-y-3">
            {Object.entries(settings.notifications).map(([key, value]) => (
              <div
                key={key}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-dark-400/30 rounded-lg"
              >
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </span>
                <button
                  onClick={() => updateSettings(`notifications.${key}`, !value)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    value ? 'bg-green-600' : 'bg-gray-300 dark:bg-dark-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      value ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Feature Flags */}
        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-300/40">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <FaFlag className="h-5 w-5" />
              Feature Flags
            </h2>
          </div>
          <div className="p-6 space-y-3">
            {Object.entries(settings.featureFlags).map(([key, value]) => (
              <div
                key={key}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-dark-400/30 rounded-lg"
              >
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </span>
                <button
                  onClick={() => updateSettings(`featureFlags.${key}`, !value)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    value ? 'bg-green-600' : 'bg-gray-300 dark:bg-dark-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      value ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Cache Management */}
        <div className="lg:col-span-2 bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-300/40 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <FaDatabase className="h-5 w-5" />
              Cache Management
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={fetchCacheStats}
                disabled={loadingCacheStats}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-400 border border-gray-300 dark:border-dark-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-300 transition-colors"
              >
                <FaSyncAlt className={`h-3 w-3 ${loadingCacheStats ? 'animate-spin' : ''}`} />
                Refresh Stats
              </button>
              <button
                onClick={handleFlushCache}
                disabled={flushingCache}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
              >
                <FaTrash className="h-3 w-3" />
                {flushingCache ? 'Flushing...' : 'Flush All'}
              </button>
            </div>
          </div>
          <div className="p-6">
            {/* Caching Toggles */}
            <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-primary-500/10 rounded-lg border border-blue-200 dark:border-primary-500/30">
                <div>
                  <span className="text-sm font-semibold text-blue-700 dark:text-primary-400">Player Caching</span>
                  <p className="text-xs text-blue-600 dark:text-primary-500 mt-1">Cache responses for player/user requests</p>
                </div>
                <button
                  onClick={() => updateSettings('caching', !settings.caching)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.caching ? 'bg-blue-600' : 'bg-gray-300 dark:bg-dark-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.caching ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <div className="flex items-center justify-between p-4 bg-purple-50 dark:bg-purple-500/10 rounded-lg border border-purple-200 dark:border-purple-500/30">
                <div>
                  <span className="text-sm font-semibold text-purple-700 dark:text-purple-400">Admin Caching</span>
                  <p className="text-xs text-purple-600 dark:text-purple-500 mt-1">Cache responses for admin operations</p>
                </div>
                <button
                  onClick={() => updateSettings('adminCaching', !settings.adminCaching)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.adminCaching ? 'bg-purple-600' : 'bg-gray-300 dark:bg-dark-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.adminCaching ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Cache Stats */}
            {cacheStats && (
              <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-gray-50 dark:bg-dark-400/30 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                    <FaServer className="h-4 w-4" />
                    <span className="text-xs font-medium">Status</span>
                  </div>
                  <div className={`text-lg font-bold ${cacheStats.connected ? 'text-green-600' : 'text-red-600'}`}>
                    {cacheStats.connected ? 'Connected' : 'Disconnected'}
                  </div>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-dark-400/30 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                    <FaKey className="h-4 w-4" />
                    <span className="text-xs font-medium">Keys</span>
                  </div>
                  <div className="text-lg font-bold text-gray-900 dark:text-white">
                    {cacheStats.keyCount?.toLocaleString() ?? 'N/A'}
                  </div>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-dark-400/30 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                    <FaMemory className="h-4 w-4" />
                    <span className="text-xs font-medium">Memory Used</span>
                  </div>
                  <div className="text-lg font-bold text-gray-900 dark:text-white">
                    {cacheStats.memoryUsage?.used ?? 'N/A'}
                  </div>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-dark-400/30 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                    <FiClock className="h-4 w-4" />
                    <span className="text-xs font-medium">Uptime</span>
                  </div>
                  <div className="text-lg font-bold text-gray-900 dark:text-white">
                    {cacheStats.uptime ? formatUptime(cacheStats.uptime) : 'N/A'}
                  </div>
                </div>
              </div>
            )}

            {!cacheStats && !loadingCacheStats && (
              <div className="mb-6 p-4 bg-gray-50 dark:bg-dark-400/30 rounded-lg text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Click "Refresh Stats" to load cache statistics
                </p>
              </div>
            )}

            {loadingCacheStats && (
              <div className="mb-6 p-4 bg-gray-50 dark:bg-dark-400/30 rounded-lg text-center">
                <FaSyncAlt className="h-6 w-6 text-blue-500 animate-spin mx-auto" />
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Loading cache stats...</p>
              </div>
            )}

            {/* Cache Operations */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Delete by Pattern */}
              <div className="p-4 bg-gray-50 dark:bg-dark-400/30 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <FaSearch className="h-4 w-4" />
                  Delete by Pattern
                </h4>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={deletePattern}
                    onChange={(e) => setDeletePattern(e.target.value)}
                    placeholder="e.g., contest:*, series:123*"
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={handleDeletePattern}
                    disabled={deletingPattern || !deletePattern.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {deletingPattern ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>

              {/* Delete Specific Key */}
              <div className="p-4 bg-gray-50 dark:bg-dark-400/30 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <FaKey className="h-4 w-4" />
                  Delete Specific Key
                </h4>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={deleteKey}
                    onChange={(e) => setDeleteKey(e.target.value)}
                    placeholder="e.g., contest:123:leaderboard"
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={handleDeleteKey}
                    disabled={deletingKey || !deleteKey.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {deletingKey ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>

              {/* Clear Series Cache */}
              <div className="p-4 bg-gray-50 dark:bg-dark-400/30 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <FaFlag className="h-4 w-4" />
                  Clear Series Cache
                </h4>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={clearSeriesId}
                    onChange={(e) => setClearSeriesId(e.target.value)}
                    placeholder="Series ID"
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={handleClearSeriesCache}
                    disabled={clearingSeriesCache || !clearSeriesId.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {clearingSeriesCache ? 'Clearing...' : 'Clear'}
                  </button>
                </div>
              </div>

              {/* Clear Contest Cache */}
              <div className="p-4 bg-gray-50 dark:bg-dark-400/30 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <FaBell className="h-4 w-4" />
                  Clear Contest Cache
                </h4>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={clearContestId}
                    onChange={(e) => setClearContestId(e.target.value)}
                    placeholder="Contest ID"
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={handleClearContestCache}
                    disabled={clearingContestCache || !clearContestId.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {clearingContestCache ? 'Clearing...' : 'Clear'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Maintenance Mode */}
        <div className="lg:col-span-2 bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-300/40">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <FaWrench className="h-5 w-5" />
              Maintenance Mode
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-center justify-between p-4 bg-red-50 dark:bg-danger-500/10 rounded-lg border border-red-200 dark:border-danger-500/30">
                <div>
                  <span className="text-sm font-semibold text-red-700 dark:text-danger-400">Enable Maintenance</span>
                  <p className="text-xs text-red-600 dark:text-danger-500 mt-1">Users will see maintenance page</p>
                </div>
                <button
                  onClick={() => updateSettings('maintenance.isEnabled', !settings.maintenance.isEnabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.maintenance.isEnabled ? 'bg-red-600' : 'bg-gray-300 dark:bg-dark-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.maintenance.isEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <div className="flex items-center justify-between p-4 bg-yellow-50 dark:bg-warning-500/10 rounded-lg border border-yellow-200 dark:border-warning-500/30">
                <div>
                  <span className="text-sm font-semibold text-yellow-700 dark:text-warning-400">Allow Admin Access</span>
                  <p className="text-xs text-yellow-600 dark:text-warning-500 mt-1">Admins can bypass maintenance</p>
                </div>
                <button
                  onClick={() => updateSettings('maintenance.allowAdminAccess', !settings.maintenance.allowAdminAccess)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.maintenance.allowAdminAccess ? 'bg-yellow-600' : 'bg-gray-300 dark:bg-dark-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.maintenance.allowAdminAccess ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Maintenance Message
                </label>
                <textarea
                  value={settings.maintenance.message}
                  onChange={(e) => updateSettings('maintenance.message', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500 resize-none"
                  placeholder="We are currently under maintenance..."
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Info */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm text-gray-500 dark:text-gray-400 p-4 bg-gray-50 dark:bg-dark-400/30 rounded-lg">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <FiClock className="h-4 w-4" />
            Created: {formatDate(settings.createdAt)}
          </span>
          <span className="flex items-center gap-1">
            <FiClock className="h-4 w-4" />
            Updated: {formatDate(settings.updatedAt)}
          </span>
        </div>
        <span className="mt-2 sm:mt-0 font-mono text-xs">ID: {settings.id}</span>
      </div>
    </div>
  );
};

export default AppSettings;
